import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CinematicBackground from "@/components/CinematicBackground";
import ImagePicker from "@/components/ImagePicker";
import ResultView from "@/components/ResultView";
import { DEMO_SCENE } from "@/constants/images";
import { useApp } from "@/context/AppContext";
import { useLang, useCinema } from "@/i18n/LanguageContext";
import { toast } from "@/components/ui/sonner";

export default function SceneAnalysis() {
  const { sharedImage, setSharedImage } = useApp();
  const { t, lang } = useLang();
  const { SCENE_TYPES, SCENE_ANALYSIS } = useCinema();
  const [image, setImage] = useState(sharedImage || null);
  const [scene, setScene] = useState("drama");
  const [phase, setPhase] = useState("idle"); // idle | scanning | done

  const analysis = SCENE_ANALYSIS[scene];
  const rtl = lang === "ar";

  const handleImage = (img) => { setImage(img); setSharedImage(img); setPhase("idle"); };

  const analyze = () => {
    if (!image) { setImage(DEMO_SCENE); setSharedImage(DEMO_SCENE); }
    setPhase("scanning");
    setTimeout(() => setPhase("done"), 2600);
  };

  return (
    <div className="relative px-4 pt-7" dir={rtl ? "rtl" : "ltr"} data-testid="scene-analysis-page">
      <CinematicBackground scanning={phase === "scanning"} />
      <PageHeader title={t("analysis_title")} icon="fa-brain" />

      {/* المعاينة */}
      <div className="glass rounded-3xl p-3 mb-4 relative overflow-hidden">
        <div className="relative rounded-2xl overflow-hidden bg-black/40 aspect-video flex items-center justify-center">
          {image ? (
            <img src={image} alt="preview" className="w-full h-full object-cover" data-testid="analysis-preview" />
          ) : (
            <div className="text-center text-slate-500 py-8">
              <i className="fa-solid fa-image text-3xl mb-2" />
              <p className="text-sm">{t("preview_hint")}</p>
            </div>
          )}
          {phase === "scanning" && (
            <div className="absolute inset-0">
              <div className="scan-line" style={{ top: 0 }} />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[1px]">
                <div className="text-center">
                  <i className="fa-solid fa-brain text-ai text-4xl focus-pulse" />
                  <p className="text-ai text-sm mt-3 font-display">{t("scanning")}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="mt-3">
          <ImagePicker onImage={handleImage} testIdPrefix="analysis" />
        </div>
      </div>

      {/* اختيار نوع المشهد */}
      <h3 className="font-display text-slate-200 mb-2">{t("sceneType")}</h3>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {SCENE_TYPES.map((s) => (
          <button
            key={s.id}
            data-testid={`scene-${s.id}`}
            onClick={() => { setScene(s.id); setPhase("idle"); }}
            className="glass rounded-2xl py-3 flex flex-col items-center gap-1 transition"
            style={scene === s.id ? { borderColor: s.color, boxShadow: `0 0 16px ${s.color}55` } : {}}
          >
            <i className={`fa-solid ${s.emoji} text-lg`} style={{ color: scene === s.id ? s.color : "#94a3b8" }} />
            <span className={`text-[11px] ${scene === s.id ? "text-slate-100 font-bold" : "text-slate-400"}`}>{s.name}</span>
          </button>
        ))}
      </div>

      <button
        data-testid="analyze-btn"
        onClick={analyze}
        disabled={phase === "scanning"}
        className="btn-gold w-full rounded-2xl py-4 font-display text-lg flex items-center justify-center gap-2 mb-6 disabled:opacity-60"
      >
        <i className="fa-solid fa-wand-sparkles" /> {t("analyze_btn")}
      </button>

      <AnimatePresence>
        {phase === "done" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5" data-testid="analysis-results">
            {/* قراءة تحليل المشهد */}
            <div className="glass rounded-3xl p-5">
              <h3 className="font-display text-lg text-ai mb-3 flex items-center gap-2"><i className="fa-solid fa-magnifying-glass-chart" /> {t("readingScene")}</h3>
              <div className="grid gap-2">
                {Object.entries(analysis.readout).map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between gap-3 border-b border-white/5 pb-2 last:border-0">
                    <span className="text-xs text-slate-400 shrink-0">{k}</span>
                    {Array.isArray(v) ? (
                      <span className="flex gap-1">{v.map((c) => <span key={c} className="w-5 h-5 rounded-md border border-white/20" style={{ background: c }} />)}</span>
                    ) : (
                      <span className="text-xs text-slate-100 text-end">{v}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* التوصيات */}
            <div>
              <h3 className="font-display text-lg text-gold mb-3 flex items-center gap-2"><i className="fa-solid fa-list-check" /> {t("recommendations")}</h3>
              <div className="space-y-3">
                {analysis.recs.map((r, i) => (
                  <RecCard key={i} title={r[0]} value={r[1]} reason={r[2]} apply={r[3]} confidence={r[4]} t={t} />
                ))}
              </div>
            </div>

            {/* صفحة النتيجة */}
            <h3 className="font-display text-lg gold-gradient-text mt-6 mb-1 flex items-center gap-2"><i className="fa-solid fa-award" /> {t("result")}</h3>
            <ResultView result={analysis} image={image || DEMO_SCENE} filterId={analysis.filter} onReanalyze={() => { setPhase("scanning"); toast.info(t("t_reanalyzing")); setTimeout(() => setPhase("done"), 2200); }} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function RecCard({ title, value, reason, apply, confidence, t }) {
  const [open, setOpen] = useState(false);
  const conColor = confidence >= 90 ? "#7bd88f" : confidence >= 82 ? "#d9b46a" : "#f0b93a";
  return (
    <div className="glass rounded-2xl overflow-hidden" data-testid={`rec-${title}`}>
      <button onClick={() => setOpen(!open)} className="w-full p-4 flex items-center justify-between gap-3 text-start">
        <div className="flex-1">
          <p className="text-xs text-slate-400">{title}</p>
          <p className="font-display text-gold text-base">{value}</p>
        </div>
        <div className="flex flex-col items-center gap-1 w-16">
          <span className="text-[10px] text-slate-400">{t("confidence")}</span>
          <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${confidence}%`, background: conColor }} />
          </div>
          <span className="text-[10px] font-bold" style={{ color: conColor }}>{confidence}%</span>
        </div>
        <i className={`fa-solid fa-chevron-${open ? "up" : "down"} text-slate-500 text-xs`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="px-4 pb-4 space-y-2 overflow-hidden">
            <Detail label={t("reason")} text={reason} icon="fa-lightbulb" color="#4ea8ff" />
            <Detail label={t("howApply")} text={apply} icon="fa-gear" color="#d9b46a" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const Detail = ({ label, text, icon, color }) => (
  <div className="flex items-start gap-2 bg-white/5 rounded-xl p-2.5">
    <i className={`fa-solid ${icon} mt-0.5`} style={{ color }} />
    <div>
      <p className="text-[11px]" style={{ color }}>{label}</p>
      <p className="text-xs text-slate-200 leading-relaxed">{text}</p>
    </div>
  </div>
);

export function PageHeader({ title, icon }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-11 h-11 rounded-2xl glass glass-blue flex items-center justify-center">
        <i className={`fa-solid ${icon} text-gold text-lg`} />
      </div>
      <h1 className="font-display text-xl gold-gradient-text">{title}</h1>
    </div>
  );
}
